import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faUndo, faConciergeBell } from '@fortawesome/free-solid-svg-icons';
import { EditOutlined, EyeOutlined, MoreOutlined } from '@ant-design/icons';

export type ActionButtonType = 'edit' | 'view' | 'permission' | 'delete' | 'duplicate' | 'options' | 'more' | 'undo' | 'requirement';

const bgColor: Record<ActionButtonType, string> = {
  edit: '#e6f4ff',
  view: '#b5f5ec',
  permission: '#f6ffed',
  delete: '#fff1f0',
  duplicate: '#fff7e6',
  options: '#f9f0ff',
  more: '#f1f3f5',
  undo: '#fffbe6',
  requirement: '#fff7e6',
};

const bgColorHover: Record<ActionButtonType, string> = {
  edit: '#bae0ff',
  view: '#87e8de',
  permission: '#d9f7be',
  delete: '#ffccc7',
  duplicate: '#ffd591',
  options: '#efdbff',
  more: '#cbd5e1',
  undo: '#fff1b8',
  requirement: '#ffd591',
};

const textColor: Record<ActionButtonType, string> = {
  edit: '#1677ff',
  view: '#08979c',
  permission: '#52c41a',
  delete: '#ff4d4f',
  duplicate: '#d46b08',
  options: '#722ed1',
  more: '#1a1a1a',
  undo: '#faad14',
  requirement: '#d46b08',
};

const borderColorHover: Record<ActionButtonType, string> = {
  edit: '#1677ff',
  view: '#08979c',
  permission: '#52c41a',
  delete: '#ff4d4f',
  duplicate: '#d46b08',
  options: '#722ed1',
  more: '#94a3b8',
  undo: '#faad14',
  requirement: '#d46b08',
};

export const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledButton = styled.button<{ $type?: ActionButtonType; $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid transparent;
  background-color: ${(p) => (p.$disabled ? '#f5f5f5' : p.$type ? bgColor[p.$type] : '#f0f0f0')};
  color: ${(p) => (p.$disabled ? '#bfbfbf' : p.$type ? textColor[p.$type] : '#595959')};
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  outline: none;
  padding: 0;

  &:hover {
    ${(p) =>
      !p.$disabled &&
      `
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      ${p.$type ? `background-color: ${bgColorHover[p.$type]}; border-color: ${borderColorHover[p.$type]};` : 'background-color: #e0e0e0; border-color: #bfbfbf;'}
    `}
  }

  &:active {
    ${(p) => !p.$disabled && 'transform: translateY(0);'}
  }
`;

interface ActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  $type?: ActionButtonType;
  $disabled?: boolean;
}

export const ActionButton = React.forwardRef<HTMLButtonElement, ActionButtonProps>(
  ({ $type, $disabled, children, ...rest }, ref) => {
    const defaultIcon =
      $type === 'edit' ? <EditOutlined /> :
      $type === 'view' ? <EyeOutlined /> :
      $type === 'delete' ? <FontAwesomeIcon icon={faTrashAlt} /> :
      $type === 'more' ? <MoreOutlined /> :
      $type === 'undo' ? <FontAwesomeIcon icon={faUndo} /> :
      $type === 'requirement' ? <FontAwesomeIcon icon={faConciergeBell} /> :
      null;

    return (
      <StyledButton ref={ref} $type={$type} $disabled={$disabled} disabled={$disabled} {...rest}>
        {children ?? defaultIcon}
      </StyledButton>
    );
  }
);
ActionButton.displayName = 'ActionButton';
